import React, { useEffect, useRef, useState, useMemo, memo } from "react";
import { useChatAI } from "./chat-ai-context";
import { TextArea } from "@/components/base/textarea/textarea";
import { Button } from "@/components/base/buttons/button";
import { Paperclip, Recording01 } from "@untitledui/icons";
import { cx } from "@/utils/cx";
import { Avatar } from "@/components/base/avatar/avatar";
import { ChatInitialScreen } from "./chat-initial-screen";
import { ConversationalFormWizard } from "./conversational-form-wizard";
import { useTranslation } from "react-i18next";

// Componente memoizzato per i messaggi dell'assistente
const AssistantMessageItem = memo(({
    content,
    timestamp,
    avatar
}: {
    content: string;
    timestamp?: number;
    avatar: React.ReactElement;
}) => {
    const { t } = useTranslation(['common']);

    const formatTimestamp = (ts?: number) => {
        if (!ts) return undefined;
        try {
            return new Date(ts).toLocaleString(undefined, {
                weekday: "long",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return undefined;
        }
    };

    return (
        <div className="flex items-start mb-1">
            {avatar}
            <div className="flex-1">
                <div className="mb-1 flex items-baseline justify-between pr-1 min-w-[180px] md:min-w-[220px] gap-3">
                    <span className="text-sm font-medium text-primary">{t('aiChat.assistant')}</span>
                    {timestamp && <span className="text-xs text-tertiary">{formatTimestamp(timestamp)}</span>}
                </div>
                <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                    {content}
                </div>
            </div>
        </div>
    );
});

AssistantMessageItem.displayName = "AssistantMessageItem";

// Componente memoizzato per i messaggi dell'utente
const UserMessageItem = memo(({
    content,
    timestamp
}: {
    content: string;
    timestamp?: number;
}) => {
    const { t } = useTranslation(['common']);

    const formatTimestamp = (ts?: number) => {
        if (!ts) return undefined;
        try {
            return new Date(ts).toLocaleString(undefined, {
                weekday: "long",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return undefined;
        }
    };

    return (
        <div className="flex flex-col items-end">
            <div className="mb-1 flex items-baseline justify-between pr-1 min-w-[180px] md:min-w-[220px] gap-3">
                <span className="text-sm font-medium text-primary">{t('aiChat.you')}</span>
                {timestamp && <span className="text-xs text-tertiary">{formatTimestamp(timestamp)}</span>}
            </div>
            <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                {content}
            </div>
        </div>
    );
});

UserMessageItem.displayName = "UserMessageItem";

export const AIChatWindow = () => {
    const { messages, appendMessage, isTyping, isNewChat, activeWizard } = useChatAI();
    const [text, setText] = useState("");
    const listRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation(['common']);

    // Memoizzare l'avatar per evitare che cambi ad ogni render
    const assistantAvatarUrl = useMemo(() => {
        const avatarPool = [
            "https://i.pravatar.cc/80?img=1",
            "https://i.pravatar.cc/80?img=2",
            "https://i.pravatar.cc/80?img=3",
            "https://i.pravatar.cc/80?img=4",
            "https://i.pravatar.cc/80?img=5",
        ];
        return avatarPool[Math.floor(Math.random() * avatarPool.length)];
    }, []); // Solo una volta al mount

    // Memoizzare il componente avatar per evitare re-render inutili
    const AssistantAvatar = useMemo(() => {
        return <Avatar size="sm" src={assistantAvatarUrl} status="online" className="mr-3" />;
    }, [assistantAvatarUrl]);

    useEffect(() => {
        // autoscroll quando ci sono nuovi messaggi
        if (messages.length > 0) {
            listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [messages.length]);

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const value = text.trim();
        if (!value) return;
        appendMessage({ role: "user", content: value });
        setText("");
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            onSubmit(e);
        }
    };

    // Mostra schermata iniziale se nuova chat e nessun wizard attivo
    if (isNewChat && !activeWizard) {
        return <ChatInitialScreen />;
    }

    // Mostra wizard se attivo
    if (activeWizard) {
        return <ConversationalFormWizard />;
    }

    return (
        <div className="flex h-full min-h-0 flex-col w-full">
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 md:p-6">
                <div className="flex flex-col gap-5 w-full">
                    {messages.map((m) => (
                        <div key={m.id} className={cx(m.role === "user" ? "self-end" : "self-start")}>
                            {m.role === "assistant" && (
                                <AssistantMessageItem
                                    content={m.content}
                                    timestamp={m.timestamp}
                                    avatar={AssistantAvatar}
                                />
                            )}
                            {m.role === "user" && (
                                <UserMessageItem
                                    content={m.content}
                                    timestamp={m.timestamp}
                                />
                            )}
                        </div>
                    ))}

                    {isTyping && (
                        <div className="flex items-center">
                            {AssistantAvatar}
                            <div className="inline-flex items-center gap-2">
                                <span className="text-sm font-medium text-primary">{t('aiChat.assistant')}</span>
                                <span className="rounded-2xl border border-secondary bg-primary px-2 py-1">
                                    <span className="inline-flex items-center gap-1">
                                        <span className="size-1.5 rounded-full bg-gray-500 animate-pulse [animation-delay:-0.3s]" />
                                        <span className="size-1.5 rounded-full bg-gray-400 animate-pulse [animation-delay:-0.15s]" />
                                        <span className="size-1.5 rounded-full bg-gray-300 animate-pulse" />
                                    </span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <form onSubmit={onSubmit} className="border-t border-secondary py-3 md:py-4 px-4 md:px-6">
                <div
                    className="relative w-full rounded-lg bg-primary ring-1 ring-primary transition-shadow duration-100 ease-linear focus-within:ring-2 focus-within:ring-brand"
                >
                    {/* Top-right subtle equalizer */}
                    <div className="pointer-events-none absolute right-3 top-3 flex gap-1 opacity-50">
                        <Recording01 className="size-4 dark:text-white" />
                    </div>

                    <TextArea
                        value={text}
                        onChange={(val) => setText(val as string)}
                        rows={4}
                        placeholder={t('aiChat.placeholder')}
                        textAreaClassName={(state) =>
                            cx(
                                "min-h-[88px] resize-none border-0 bg-transparent px-3.5 pb-10 pt-3 shadow-none ring-0 outline-hidden",
                                typeof state === "string" && state,
                            )
                        }
                        onKeyDown={onKeyDown}
                        className=""
                    />

                    {/* Bottom-right actions */}
                    <div className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-2">
                        <button
                            type="button"
                            className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover"
                            aria-label={t('aiChat.attach')}
                        >
                            <Paperclip className="size-4" />
                        </button>
                        <button
                            type="button"
                            className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover"
                            aria-label={t('aiChat.emoji')}
                        >
                            <span className="block text-base leading-none">☺︎</span>
                        </button>
                        <Button className="pointer-events-auto" color="link-color" size="md" type="submit">
                            {t('buttons.send')}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
};


